import { type ReactNode, lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Loader2 } from 'lucide-react';
import type { RolUsuario } from '../types';

// Pages - Public
import LoginPage from '../pages/LoginPage';
import RegistroPage from '../pages/RegistroPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import MagicLinkCallbackPage from '../pages/MagicLinkCallbackPage';

// Pages - Protected
import PerfilPage from '../pages/PerfilPage';
import SalasPage from '../pages/SalasPage';
import VideoCallPageV2 from '../pages/VideoCallPageV2';
import AlertasPage from '../pages/admin/AlertasPage';
import SupervisorObservarPage from '../pages/SupervisorObservarPage';
import SupervisionPage from '../pages/SupervisionPage';
import HistorialPage from '../pages/HistorialPage';
import ConsultaDetallePage from '../pages/ConsultaDetallePage';
import ConfigSalasPage from '../pages/ConfigSalasPage';
import SalaFormPage from '../pages/SalaFormPage';
import SesionesPage from '../pages/SesionesPage';
import PostConsultPage from '../pages/PostConsultPage';
import PacienteHistorialPage from '../pages/PacienteHistorialPage';

// Pages - Admin
import AdminDashboardPage from '../pages/admin/AdminDashboardPage';
import GestionMedicosPage from '../pages/admin/GestionMedicosPage';
import ConfigGlobalPage from '../pages/admin/ConfigGlobalPage';
import AuditoriaPage from '../pages/admin/AuditoriaPage';
import AuditoriaAccesosPage from '../pages/admin/AuditoriaAccesosPage';
import AIPromptsPage from '../pages/admin/TriagePromptPage';
import ConfigIAPage from '../pages/admin/ConfigIAPage';
import UsoIAPage from '../pages/admin/UsoIAPage';
import FondosVirtualesPage from '../pages/admin/FondosVirtualesPage';
import RegionesTeleconsultaPage from '../pages/admin/RegionesTeleconsultaPage';
import MonitorEnVivoPage from '../pages/admin/MonitorEnVivoPage';
// El piso 3D trae Three.js (~600 kB): se carga solo al abrir la página.
const MonitorPiso3DPage = lazy(() => import('../pages/admin/MonitorPiso3DPage'));
import MonitorLogsPage from '../pages/admin/MonitorLogsPage';
import TrazabilidadPage from '../pages/admin/TrazabilidadPage';
import SeguridadAccesoPage from '../pages/admin/SeguridadAccesoPage';
import PruebasListaPage from '../pages/admin/PruebasListaPage';
import PruebaDetallePage from '../pages/admin/PruebaDetallePage';

// Pages - Monitoreo en televisor (rol visor + supervisor + admin)
import TvOperacionPage from '../pages/tv/TvOperacionPage';
import TvMonitorPage from '../pages/tv/TvMonitorPage';

// Pages - Notificaciones (campañas push FCM)
import CampanasPage from '../pages/notificaciones/CampanasPage';
import CampanaComposerPage from '../pages/notificaciones/CampanaComposerPage';
import CampanaDetallePage from '../pages/notificaciones/CampanaDetallePage';
import DispositivosPruebaPage from '../pages/notificaciones/DispositivosPruebaPage';

// Pages - Servicio de Correo (GPP-202603: administración del servicio SES, solo admin)
import CorreoLayout from '../pages/admin/correo/CorreoLayout';
import EstadisticasCorreoPage from '../pages/admin/correo/EstadisticasCorreoPage';
import HistorialCorreoPage from '../pages/admin/correo/HistorialCorreoPage';
import CatalogosCorreoPage from '../pages/admin/correo/CatalogosCorreoPage';
import AplicacionesCorreoPage from '../pages/admin/correo/AplicacionesCorreoPage';
import NoEntregablesCorreoPage from '../pages/admin/correo/NoEntregablesCorreoPage';
import AuditoriaCorreoPage from '../pages/admin/correo/AuditoriaCorreoPage';
// Pages - Registro biométrico (KYC): reemplaza al dashboard KYC independiente, solo admin
import KycLayout from '../pages/admin/kyc/KycLayout';
import KycDashboardPage from '../pages/admin/kyc/KycDashboardPage';
import KycValidacionesPage from '../pages/admin/kyc/KycValidacionesPage';
// Pages - Uso de la app móvil (estadísticas de uso por plataforma, pantallas y funciones), solo admin
import UsoAppPage from '../pages/admin/uso-app/UsoAppPage';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: RolUsuario | RolUsuario[];
}

/** Adónde mandar a cada rol cuando pide algo que no le corresponde. */
const INICIO_POR_ROL: Record<string, string> = {
  notificador: '/notificaciones',
  visor: '/tv',
};

function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, loading, role } = useAuth();
  const { addToast } = useToast();
  const location = useLocation();

  // Check role if required — FAIL-CLOSED: si la ruta exige un rol y el usuario
  // no tiene rol (o no coincide), se deniega. (SEC-10)
  const hasRequiredRole = !!requiredRole;
  const allowed = requiredRole
    ? (role
      ? (Array.isArray(requiredRole) ? requiredRole.includes(role) : requiredRole === role)
      : false)
    : true;

  // El toast es un side-effect y debe dispararse solo una vez por transición de
  // bloqueo (no en cada render). requiredRole suele pasarse como array literal
  // inline (nueva referencia en cada render), por lo que las deps usan solo
  // primitivos derivados (hasRequiredRole/allowed) en vez de requiredRole.
  useEffect(() => {
    if (!loading && user && hasRequiredRole && !allowed) {
      // Sin rol no es lo mismo que rol equivocado: lo primero suele ser que los
      // permisos no llegaron (token sin refrescar) y se resuelve recargando;
      // el aviso genérico dejaba al usuario sin saber qué pasó (issue #43).
      addToast(
        'error',
        role
          ? `Su perfil (${role}) no tiene acceso a esta sección.`
          : 'No se pudieron cargar sus permisos. Recargue la página o vuelva a iniciar sesión.',
      );
    }
  }, [loading, user, role, hasRequiredRole, allowed, location.pathname, addToast]);

  if (loading) {
    return (
      <div className="loading-screen">
        <Loader2 className="spinner" size={40} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (requiredRole && !allowed) {
    return <Navigate to={INICIO_POR_ROL[role ?? ''] ?? '/salas'} replace />;
  }

  // El rol notificador solo tiene acceso al módulo de notificaciones (+ perfil):
  // cualquier otra ruta sin requiredRole explícito se redirige a su módulo.
  if (role === 'notificador' && !requiredRole) {
    const permitido = location.pathname.startsWith('/notificaciones')
      || location.pathname === '/perfil';
    if (!permitido) return <Navigate to="/notificaciones" replace />;
  }

  // El rol visor existe solo para las pantallas de televisor: no ve expedientes
  // ni colas, así que cualquier otra ruta lo devuelve a su tablero.
  if (role === 'visor' && !requiredRole) {
    if (!location.pathname.startsWith('/tv')) return <Navigate to="/tv" replace />;
  }

  return <>{children}</>;
}

export default function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LoginPage />} />
      <Route path="/registro" element={<RegistroPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/magic-link-callback" element={<MagicLinkCallbackPage />} />

      {/* Protected routes - any authenticated role */}
      <Route path="/perfil" element={<ProtectedRoute><PerfilPage /></ProtectedRoute>} />
      <Route path="/salas" element={<ProtectedRoute><SalasPage /></ProtectedRoute>} />
      {/* La cola por sala vivía aquí como página aparte y quedó a medias: los
          mismos pacientes, sin el control de turno y sin poder ver el resto de
          salas. Ahora /salas es la única cola y filtra por sala cuando hay más
          de una; la URL vieja sobrevive como redirección. */}
      <Route path="/sala/:salaId" element={<Navigate to="/salas" replace />} />
      <Route path="/consulta/:id/video-ai" element={<ProtectedRoute><VideoCallPageV2 /></ProtectedRoute>} />
      <Route path="/consulta/:id/resumen" element={<ProtectedRoute><PostConsultPage /></ProtectedRoute>} />
      {/* Supervisor/admin observa una teleconsulta en curso (SUP-01) */}
      <Route path="/consulta/:id/observar" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><SupervisorObservarPage /></ProtectedRoute>} />
      <Route path="/historial" element={<ProtectedRoute><HistorialPage /></ProtectedRoute>} />
      <Route path="/historial/:consultaId" element={<ProtectedRoute><ConsultaDetallePage /></ProtectedRoute>} />
      <Route path="/paciente/:pacienteId/historial" element={<ProtectedRoute><PacienteHistorialPage /></ProtectedRoute>} />
      {/* Dashboard del médico — mismo componente, filtra a sus propios indicadores por rol */}
      <Route path="/dashboard" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />

      {/* Protected routes - admin only */}
      <Route path="/sesiones" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><SesionesPage /></ProtectedRoute>} />
      {/* SUP-01: cockpit de supervisión (en espera + en atención, por sala) */}
      <Route path="/supervision" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><SupervisionPage /></ProtectedRoute>} />
      {/* Configurar salas: EXCLUSIVO del Supervisor (el Administrador ya no opera salas) */}
      <Route path="/config/salas" element={<ProtectedRoute requiredRole="supervisor"><ConfigSalasPage /></ProtectedRoute>} />
      <Route path="/config/salas/nueva" element={<ProtectedRoute requiredRole="supervisor"><SalaFormPage /></ProtectedRoute>} />
      <Route path="/config/salas/:salaId/editar" element={<ProtectedRoute requiredRole="supervisor"><SalaFormPage /></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/monitor" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><MonitorEnVivoPage /></ProtectedRoute>} />
      <Route path="/admin/piso-3d" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><Suspense fallback={null}><MonitorPiso3DPage /></Suspense></ProtectedRoute>} />
      <Route path="/admin/eventos" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><MonitorLogsPage /></ProtectedRoute>} />
      <Route path="/admin/alertas" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><AlertasPage /></ProtectedRoute>} />
      {/* Trazabilidad: también para el rol VISOR — es una pantalla de
          monitoreo de solo lectura, sin acciones sobre la operación. */}
      <Route path="/admin/trazabilidad" element={<ProtectedRoute requiredRole={['admin', 'supervisor', 'visor']}><TrazabilidadPage /></ProtectedRoute>} />
      {/* Legacy URL — preserved as redirect to the unified Médicos page */}
      <Route path="/admin/pre-registro" element={<Navigate to="/admin/medicos" replace />} />
      <Route path="/admin/medicos" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><GestionMedicosPage /></ProtectedRoute>} />
      {/* Configuración global: EXCLUSIVO del Supervisor */}
      <Route path="/admin/configuracion" element={<ProtectedRoute requiredRole="supervisor"><ConfigGlobalPage /></ProtectedRoute>} />
      <Route path="/admin/auditoria" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><AuditoriaPage /></ProtectedRoute>} />
      <Route path="/admin/auditoria-accesos" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><AuditoriaAccesosPage /></ProtectedRoute>} />
      {/* Segundo factor: el supervisor ve el padrón, el administrador lo opera.
          El backend vuelve a comprobar el rol en cada acción. */}
      <Route path="/admin/seguridad" element={<ProtectedRoute requiredRole={['admin', 'supervisor']}><SeguridadAccesoPage /></ProtectedRoute>} />
      {/* Prompts IA y Configurar IA: EXCLUSIVO del Administrador */}
      <Route path="/admin/ai-prompts" element={<ProtectedRoute requiredRole="admin"><AIPromptsPage /></ProtectedRoute>} />
      {/* Legacy route redirect */}
      <Route path="/admin/triage-prompt" element={<ProtectedRoute requiredRole="admin"><AIPromptsPage /></ProtectedRoute>} />
      <Route path="/admin/config-ia" element={<ProtectedRoute requiredRole="admin"><ConfigIAPage /></ProtectedRoute>} />
      <Route path="/admin/uso-ia" element={<ProtectedRoute requiredRole="admin"><UsoIAPage /></ProtectedRoute>} />
      {/* Personalización (Fondos Virtuales, Regiones): EXCLUSIVO del Supervisor */}
      <Route path="/admin/fondos-virtuales" element={<ProtectedRoute requiredRole="supervisor"><FondosVirtualesPage /></ProtectedRoute>} />
      <Route path="/admin/regiones-teleconsulta" element={<ProtectedRoute requiredRole="supervisor"><RegionesTeleconsultaPage /></ProtectedRoute>} />
      <Route path="/admin/pruebas" element={<ProtectedRoute requiredRole="admin"><PruebasListaPage /></ProtectedRoute>} />
      <Route path="/admin/pruebas/:id" element={<ProtectedRoute requiredRole="admin"><PruebaDetallePage /></ProtectedRoute>} />

      {/* Servicio de Correo — administración del servicio SES (GPP-202603), EXCLUSIVO del
          Administrador. Rutas anidadas: CorreoLayout aporta el chrome + pestañas y el <Outlet>. */}
      <Route path="/admin/correo" element={<ProtectedRoute requiredRole="admin"><CorreoLayout /></ProtectedRoute>}>
        <Route index element={<EstadisticasCorreoPage />} />
        <Route path="historial" element={<HistorialCorreoPage />} />
        <Route path="catalogos" element={<CatalogosCorreoPage />} />
        <Route path="aplicaciones" element={<AplicacionesCorreoPage />} />
        <Route path="no-entregables" element={<NoEntregablesCorreoPage />} />
        <Route path="auditoria" element={<AuditoriaCorreoPage />} />
      </Route>

      {/* Registro biométrico (KYC) — EXCLUSIVO del Administrador. Cada ambiente del portal ve
          los datos de SU ambiente de AWS; lo decide el backend, no el navegador. */}
      <Route path="/admin/registro-biometrico" element={<ProtectedRoute requiredRole="admin"><KycLayout /></ProtectedRoute>}>
        <Route index element={<KycDashboardPage />} />
        <Route path="validaciones" element={<KycValidacionesPage />} />
      </Route>
      <Route path="/admin/uso-app" element={<ProtectedRoute requiredRole="admin"><UsoAppPage /></ProtectedRoute>} />

      {/* Monitoreo en televisor — pantallas sin operador, sin datos de pacientes.
          Abiertas al visor (rol creado para esto), al supervisor y al admin. */}
      <Route path="/tv" element={<Navigate to="/tv/operacion" replace />} />
      <Route path="/tv/operacion" element={<ProtectedRoute requiredRole={['visor', 'supervisor', 'admin']}><TvOperacionPage /></ProtectedRoute>} />
      <Route path="/tv/monitor" element={<ProtectedRoute requiredRole={['visor', 'supervisor', 'admin']}><TvMonitorPage /></ProtectedRoute>} />

      {/* Notificaciones — campañas push (admin + notificador) */}
      <Route path="/notificaciones" element={<ProtectedRoute requiredRole={['admin', 'notificador']}><CampanasPage /></ProtectedRoute>} />
      <Route path="/notificaciones/nueva" element={<ProtectedRoute requiredRole={['admin', 'notificador']}><CampanaComposerPage /></ProtectedRoute>} />
      <Route path="/notificaciones/pruebas" element={<ProtectedRoute requiredRole={['admin', 'notificador']}><DispositivosPruebaPage /></ProtectedRoute>} />
      <Route path="/notificaciones/:id" element={<ProtectedRoute requiredRole={['admin', 'notificador']}><CampanaDetallePage /></ProtectedRoute>} />
      <Route path="/notificaciones/:id/editar" element={<ProtectedRoute requiredRole={['admin', 'notificador']}><CampanaComposerPage /></ProtectedRoute>} />
    </Routes>
  );
}
